// src/contexts/ModeContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../utils";
const ModeContext = createContext();

export const useMode = () => useContext(ModeContext);

const normalizeMode = (value, fallback = "webSurrogate") => {
  const v = String(value || "").toLowerCase();
  if (v === "acris-legacy" || v === "acrislegacy") return "acris-legacy";
  if (v === "legacy") return "legacy";
  if (v === "violation") return "violation";
  if (v === "violations-v2" || v === "violation-v2" || v === "violationsv2") return "violations-v2";
  if (v === "websurrogate" || v === "web" || v === "ws") return "webSurrogate";
  return fallback;
};

const isKnownMode = (value) => {
  const v = String(value || "").toLowerCase();
  return (
    v === "acris-legacy" ||
    v === "acrislegacy" ||
    v === "legacy" ||
    v === "violation" ||
    v === "violations-v2" ||
    v === "violation-v2" ||
    v === "violationsv2" ||
    v === "websurrogate" ||
    v === "web" ||
    v === "ws"
  );
};

const modeFromPath = (pathname) => {
  const path = String(pathname || "").toLowerCase();
  if (path.startsWith("/acris-legacy")) return "acris-legacy";
  if (path.startsWith("/violations-v2")) return "violations-v2";
  if (path.startsWith("/violations")) return "violation";
  if (path.startsWith("/legacy")) return "legacy";
  if (path.startsWith("/websurrogate")) return "webSurrogate";
  return "webSurrogate";
};

/**
 * ModeProvider
 * - initialMode default is "webSurrogate"
 * - persists to localStorage for instant UX
 * - attempts to GET /api/user/mode on mount and POST /api/user/mode on change (if available)
 */
export const ModeProvider = ({ children, initialMode = "webSurrogate" }) => {
  const [mode, setModeState] = useState(() => {
    try {
      const stored = localStorage.getItem("app_mode");
      if (stored) return normalizeMode(stored, initialMode);
      const inferred = modeFromPath(window?.location?.pathname);
      return normalizeMode(inferred, initialMode);
    } catch {
      return normalizeMode(initialMode, "webSurrogate");
    }
  });
  const [loading, setLoading] = useState(false);

  // changeMode persists locally and attempts to update backend
  const changeMode = async (newMode) => {
    const normalized = normalizeMode(newMode, mode);
    if (!["webSurrogate", "legacy", "violation", "violations-v2", "acris-legacy"].includes(normalized)) return;
    setModeState(normalized);
    try {
      localStorage.setItem("app_mode", normalized);
    } catch {}
    // try to persist on server if API exists
    try {
      setLoading(true);
      await axios.post(`${BASE_URL}/api/user/mode`, { mode: normalized });
    } catch {
      // silent fail - backend may not exist or user not authenticated
    } finally {
      setLoading(false);
    }
  };

  // On mount, try fetch from server preference
  useEffect(() => {
    let mounted = true;
    try {
      const stored = localStorage.getItem("app_mode");
      if (!stored) {
        const inferred = modeFromPath(window?.location?.pathname);
        const normalized = normalizeMode(inferred);
        setModeState(normalized);
        localStorage.setItem("app_mode", normalized);
      }
    } catch {}
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/user/mode`);
        if (!mounted) return;
        const remoteMode = res?.data?.mode;
        const stored = localStorage.getItem("app_mode");
        if (!stored && isKnownMode(remoteMode)) {
          const normalized = normalizeMode(remoteMode);
          setModeState(normalized);
          try {
            localStorage.setItem("app_mode", normalized);
          } catch {}
        }
      } catch {
        // ignore
      }
    })();
    return () => (mounted = false);
  }, []);

  return (
    <ModeContext.Provider value={{ mode, setMode: changeMode, loading }}>
      {children}
    </ModeContext.Provider>
  );
};
